import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { TrendingUp, TrendingDown, DollarSign, Clock, Activity } from 'lucide-react';
import { getApiUrl } from '../utils/api';

/**
 * MM audit 2.4: Daily PnL attribution.
 *
 * Читает GET /api/market-maker/pnl/daily?hours=24 → показывает:
 * - realized_pnl_usd (FIFO)
 * - unrealized_pnl_usd (market-to-market)
 * - fees_paid_usd (0.3% × volume)
 * - buy/sell volume + count
 * - open position + avg cost
 * - последние 10 FIFO matches.
 *
 * Auto-refresh каждые 30 сек. Никаких моков — только live endpoint.
 */
const API_URL = getApiUrl();

const fmt = (n, digits = 2) => {
  if (typeof n !== 'number' || !isFinite(n)) return '—';
  return n.toLocaleString('en-US', { maximumFractionDigits: digits, minimumFractionDigits: digits });
};

const pnlColor = (v) => {
  if (typeof v !== 'number' || v === 0) return 'text-slate-400';
  return v > 0 ? 'text-emerald-500' : 'text-red-500';
};

const DailyPnLCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const fetchIt = async () => {
    try {
      const r = await fetch(`${API_URL}/api/market-maker/pnl/daily?hours=24`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const body = await r.json();
      setData(body.breakdown);
      setErr(null);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIt();
    const t = setInterval(fetchIt, 30000);
    return () => clearInterval(t);
  }, []);

  if (loading && !data) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">MM PnL — last 24h</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-slate-400">Loading...</div>
        </CardContent>
      </Card>
    );
  }

  if (err && !data) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">MM PnL — last 24h</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-400 text-sm">{err}</div>
        </CardContent>
      </Card>
    );
  }

  const d = data || {};
  const realized = d.realized_pnl_usd || 0;
  const unrealized = d.unrealized_pnl_usd || 0;
  const total = realized + unrealized;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <DollarSign className="h-4 w-4" /> MM PnL — last 24h (FIFO)
          </span>
          <Badge variant="outline" className="text-xs">
            <Clock className="h-3 w-3 mr-1" />
            refresh 30s
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <div className="text-xs text-slate-400">Realized</div>
            <div className={`text-xl font-bold ${pnlColor(realized)}`}>
              {realized >= 0 ? '+' : ''}${fmt(realized)}
              {realized >= 0 ? (
                <TrendingUp className="h-4 w-4 inline ml-1" />
              ) : (
                <TrendingDown className="h-4 w-4 inline ml-1" />
              )}
            </div>
            <div className="text-xs text-slate-500">{d.matches_count || 0} FIFO matches</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Unrealized</div>
            <div className={`text-xl font-bold ${pnlColor(unrealized)}`}>
              {unrealized >= 0 ? '+' : ''}${fmt(unrealized)}
            </div>
            <div className="text-xs text-slate-500">
              pos {fmt(d.current_position_tokens || 0, 0)} @ avg ${fmt(d.current_avg_cost_usd || 0, 8)}
            </div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Total (R+U)</div>
            <div className={`text-xl font-bold ${pnlColor(total)}`}>
              {total >= 0 ? '+' : ''}${fmt(total)}
            </div>
            <div className="text-xs text-slate-500">24h combined</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Fees paid</div>
            <div className="text-xl font-bold text-slate-300">${fmt(d.fees_paid_usd || 0)}</div>
            <div className="text-xs text-slate-500">≈ 0.3% DEX fee</div>
          </div>
          <div>
            <div className="text-xs text-slate-400">Volume 24h</div>
            <div className="text-xl font-bold text-slate-300">
              ${fmt((d.total_buy_volume_usd || 0) + (d.total_sell_volume_usd || 0))}
            </div>
            <div className="text-xs text-slate-500">
              <Activity className="h-3 w-3 inline mr-1" />
              {d.buy_count || 0} buy / {d.sell_count || 0} sell
            </div>
          </div>
        </div>

        {Array.isArray(d.sample_matches) && d.sample_matches.length > 0 && (
          <details className="mt-4">
            <summary className="cursor-pointer text-xs text-slate-400 hover:text-slate-200">
              Last {d.sample_matches.length} FIFO matches (profit per close)
            </summary>
            <table className="mt-2 w-full text-xs">
              <thead>
                <tr className="text-slate-500">
                  <th className="text-left">Time</th>
                  <th className="text-right">Tokens</th>
                  <th className="text-right">Buy $</th>
                  <th className="text-right">Sell $</th>
                  <th className="text-right">PnL $</th>
                </tr>
              </thead>
              <tbody>
                {d.sample_matches.map((m, i) => (
                  <tr key={i} className="border-t border-slate-800">
                    <td className="text-slate-400">{m.ts?.slice(0, 19) || ''}</td>
                    <td className="text-right">{fmt(m.tokens, 0)}</td>
                    <td className="text-right">${fmt(m.buy_price, 8)}</td>
                    <td className="text-right">${fmt(m.sell_price, 8)}</td>
                    <td className={`text-right font-mono ${pnlColor(m.pnl_usd)}`}>
                      {m.pnl_usd >= 0 ? '+' : ''}${fmt(m.pnl_usd, 4)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}
      </CardContent>
    </Card>
  );
};

export default DailyPnLCard;
